<template>
<div :class="navSidebarClasses">
	<header class="nav-sidebar__header">
		<DuoToneTeamingLetterLogo
			class-name="nav-sidebar__letter-logo"
		/>
		<font-awesome-icon
			class="nav-sidebar__close-icon"
			:icon="[ 'fas', 'times', ]"
			:size="'lg'"
			@click="onCloseIconClicked"
		/>
	</header>
	<nav :class="mainClasses">
		<hr class="nav-sidebar__hr" />
		<NavSidebarGroup
			v-for="navGroup in primaryNavGroups"
			:key="navGroup.key"
			class-name="nav-sidebar__group"
			:is-selected="navGroup.isSelected"
			:scale="navGroup.scale"
			:theme="navGroup.theme"
			@click="$emit( navGroup.emits.eventName, navGroup.emits.eventArgs )"
		>
			<template v-slot:icon v-if="navGroup.icon || navGroup.avatar">
				<font-awesome-icon
					v-if="navGroup.icon"
					:icon="navGroup.icon"
				/>
				<UserAvatarSpread
					v-else-if="navGroup.avatar"
					:users="[ user ]"
				/>
			</template>
			<template v-slot:text>
				{{ navGroup.text }}
			</template>
			<template v-slot:tooltip>
				{{ navGroup.tooltip }}
			</template>
			<template v-slot:items>
				<!-- top items will always be displayed; bottom items will be hidden pending further action -->
				<ShowmoreItems>
					<template v-slot:primary v-if="navGroup.primaryItems.length">
						<NavSidebarItem
							v-for="navItem in navGroup.primaryItems"
							:key="navItem.key"
							class-name="nav-sidebar__item"
							:text="navItem.text"
							:is-selected="navItem.isSelected"
							@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
						/>
					</template>
					<template v-slot:secondary v-if="navGroup.secondaryItems.length">
						<NavSidebarItem
							v-for="navItem in navGroup.secondaryItems"
							:key="navItem.key"
							class-name="nav-sidebar__item"
							:text="navItem.text"
							:is-selected="navItem.isSelected"
							@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
						/>
					</template>
				</ShowmoreItems>
			</template>
		</NavSidebarGroup>
	</nav>
	<footer :class="footerClasses">
		<hr class="nav-sidebar__hr" />
		<NavSidebarGroup
			v-for="navGroup in secondaryNavGroups"
			:key="navGroup.key"
			class-name="nav-sidebar__group nav-sidebar__group--secondary"
			:is-selected="navGroup.isSelected"
			:scale="navGroup.scale"
			:theme="navGroup.theme"
			@click="$emit( navGroup.emits.eventName, navGroup.emits.eventArgs )"
		>
			<template v-slot:icon v-if="navGroup.icon || navGroup.avatar">
				<font-awesome-icon
					v-if="navGroup.icon"
					:icon="navGroup.icon"
				/>
				<UserAvatarSpread
					v-else-if="navGroup.avatar"
					:users="[ user ]"
				/>
			</template>
			<template v-slot:text>
				{{ navGroup.text }}
			</template>
			<template v-slot:tooltip>
				{{ navGroup.tooltip }}
			</template>
			<template v-slot:items>
				<!-- top items will always be displayed; bottom items will be hidden pending further action -->
				<ShowmoreItems>
					<template v-slot:primary v-if="navGroup.primaryItems.length">
						<NavSidebarItem
							v-for="navItem in navGroup.primaryItems"
							:key="navItem.key"
							class-name="nav-sidebar__item"
							:text="navItem.text"
							:is-selected="navItem.isSelected"
							@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
						/>
					</template>
					<template v-slot:secondary v-if="navGroup.secondaryItems.length">
						<NavSidebarItem
							v-for="navItem in navGroup.secondaryItems"
							:key="navItem.key"
							class-name="nav-sidebar__item"
							:text="navItem.text"
							:is-selected="navItem.isSelected"
							@click="$emit( navItem.emits.eventName, navItem.emits.eventArgs )"
						/>
					</template>
				</ShowmoreItems>
			</template>
		</NavSidebarGroup>
	</footer>
	<UserSettings ref="userSettings" />
</div>
</template>

<script>
import NavSidebarToggleState from "@/constants/NavSidebarToggleState";
import DuoToneTeamingLetterLogo from "@/components/DuoToneTeamingLetterLogo";
import NavSidebarGroup from "@/components/Sidebar/NavSidebarGroup";
import NavSidebarItem from "@/components/Sidebar/NavSidebarItem";
import UserAvatarSpread from "@/components/UserAvatarSpread";
import ShowmoreItems from "@/components/Sidebar/ShowmoreItems";
import UserSettings from "@/components/UserSettings";

export default {
	name: "NavSidebar",
	components: {
		DuoToneTeamingLetterLogo,
		NavSidebarGroup,
		NavSidebarItem,
		UserAvatarSpread,
		ShowmoreItems,
		UserSettings,
	},
	props: {
		user: {
			type: Object,
			required: true,
		},
		primaryNavGroups: {
			type: Array,
			required: true,
		},
		secondaryNavGroups: {
			type: Array,
			required: true,
		},
		toggleState: {
			type: String,
			required: true,
		},
	},
	computed: {
		mainClasses() {
			return {
				"nav-sidebar__main": true,
				"nav-sidebar__main--paid": !this.user.showPaymentOption,
				"nav-sidebar__main--unpaid": this.user.showPaymentOption,
			};
		},
		footerClasses() {
			return {
				"nav-sidebar__footer": true,
				"nav-sidebar__footer--paid": !this.user.showPaymentOption,
				"nav-sidebar__footer--unpaid": this.user.showPaymentOption,
			};
		},
		navSidebarClasses() {
			return {
				"nav-sidebar": true,
				"nav-sidebar--mobile": ( this.toggleState === NavSidebarToggleState.OPEN || this.toggleState === NavSidebarToggleState.CLOSED ),
				"nav-sidebar--open": this.toggleState === NavSidebarToggleState.OPEN,
				"nav-sidebar--closed": this.toggleState === NavSidebarToggleState.CLOSED,
			};
		},
	},
	methods: {
		onCloseIconClicked() {
			this.$emit( "action", {
				name: "sidebar/closeNavSidebar",
			} );
		},
	},
};
</script>

<style lang="scss" scoped>
.nav-sidebar {
	min-height: 100%;
	width: 100%;
	position: relative;
	box-shadow: inset -1px 0px 0px $gray-200;
	background: $white;
	// initially hidden on phone devices
	display: none;

	// always visible on anything larger than a phone
	@include for-size('sm') {
		display: block;
	}

}

$header-height: 104px;
$main-top: $header-height;
// NOTE: adjust main-bottom* and footer-height* TOGETHER
$main-bottom: 192px;
$main-bottom--unpaid: $main-bottom + 64px;
$main-bottom--paid: $main-bottom + 24px;
$footer-height: 216px;
$footer-height--unpaid: $footer-height + 40px;
$footer-height--paid: $footer-height;

.nav-sidebar__header {
	position: absolute;
	top: 0;
	bottom: 104px;
	height: $header-height;
	width: 100%;

	padding-top: 32px;
	padding-left: 25px;
	padding-right: 25px;

	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;

	@include for-size('sm') {
		flex-direction: column;
	}

	@include for-size('md') {
		padding-left: 0;
		padding-right: 0;
		align-items: center;
	}

	@include for-size('lg') {
		padding-left: 25px;
		padding-right: 25px;
		align-items: flex-start;
	}
}

.nav-sidebar__main {
	position: absolute;
	top: $main-top;
	bottom: $main-bottom;
	width: 100%;
	@include auto-y-scroll();
	padding: 0 24px 8px 24px;
}

.nav-sidebar__main--unpaid {
	bottom: $main-bottom--unpaid;
}

.nav-sidebar__main--paid {
	bottom: $main-bottom--paid;
}

.nav-sidebar__main::-webkit-scrollbar {
	display: none;
}

.nav-sidebar__footer {
	position: absolute;
	bottom: 0;
	width: 100%;
	height: $footer-height;

	padding: 32px 24px 0 24px;
}

.nav-sidebar__footer--unpaid {
	height: $footer-height--unpaid;
}

.nav-sidebar__footer--paid {
	height: $footer-height--paid;
}

.nav-sidebar__group {
	margin-bottom: 16.8px;
	.nav-sidebar:last-of-type {
		margin-bottom: 0;
	}
}

>>> .nav-sidebar__item {
	margin: 4px 0 4px 40px;
	text-align: left;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
}

.nav-sidebar__letter-logo {
	display: inline-flex;
}

.nav-sidebar__hr {
	margin-top: 0;
	margin-bottom: 24px;
	border-top: 1px solid $gray-200;
}

.nav-sidebar__close-icon {
	cursor: pointer;

	@include for-size('sm') {
		display: none;
	}
}

@keyframes slide-in {
	from { transform: translate( -$nav-sidebar-width ); }
	to { transform:translate( 0 ); }
}

@keyframes slide-out {
	from { transform: translate( 0 ); }
	to { transform:translate( -$nav-sidebar-width ); }
}

.nav-sidebar--mobile {
	// constrain these changes to mobile devices only
	@include for-size('xs') {
		// move this outside the layout container so that it can
		// lay over the rest of the screen content
		position: fixed;
		left: 0;
		top: 0;
		bottom: 0;
		display: block;
		width: $nav-sidebar-width;
	}
}

.nav-sidebar--open {
	// constrain these changes to mobile devices only
	@include for-size('xs') {
		animation-name: slide-in;
		animation-duration: 0.5s;
		animation-fill-mode: forwards;
	}
}

.nav-sidebar--closed {
	// constrain these changes to mobile devices only
	@include for-size('xs') {
		animation-name: slide-out;
		animation-duration: 0.5s;
		animation-fill-mode: forwards;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/Sidebar/NavSidebar.vue